import { AIAgentAnalyticsItemModel } from "@jugl-web/rest-api/integrations/models/AIAgentAnalyticsItem/AIAgentAnalyticsItemModel";
import { useMemo } from "react";
import { SentimentAnalysisDataEntry } from "../components/SentimentAnalysisWidget";

interface UseSentimentAnalysisWidgetDataOptions {
  analyticsData: AIAgentAnalyticsItemModel[];
}

export const useSentimentAnalysisWidgetData = ({
  analyticsData,
}: UseSentimentAnalysisWidgetDataOptions) => {
  const sentimentAnalysisData = useMemo(() => {
    if (!analyticsData) {
      return [];
    }

    const groupedDicts = analyticsData.reduce<{
      channelTopic: Record<string, number>;
      topicSentiment: Record<string, number>;
      sentimentHuman: Record<string, number>;
    }>(
      (acc, item) => {
        const channelTopicGroupKey = JSON.stringify([item.channel, item.topic]);

        const topicSentimentGroupKey = JSON.stringify([
          item.topic,
          item.sentiment,
        ]);

        const sentimentHumanGroupKey = JSON.stringify([
          item.sentiment,
          item.humanHandoverRequired,
        ]);

        acc.channelTopic[channelTopicGroupKey ?? 0] =
          (acc.channelTopic[channelTopicGroupKey ?? 0] ?? 0) + 1;

        acc.topicSentiment[topicSentimentGroupKey ?? 0] =
          (acc.topicSentiment[topicSentimentGroupKey ?? 0] ?? 0) + 1;

        acc.sentimentHuman[sentimentHumanGroupKey ?? 0] =
          (acc.sentimentHuman[sentimentHumanGroupKey ?? 0] ?? 0) + 1;

        return acc;
      },
      {
        channelTopic: {},
        topicSentiment: {},
        sentimentHuman: {},
      }
    );

    const data: SentimentAnalysisDataEntry[] = [];

    Object.entries(groupedDicts.channelTopic).forEach(([key, count]) => {
      const [channel, topic] = JSON.parse(key) as [string, string];

      data.push([{ name: channel, isBold: true }, { name: topic }, count]);
    });

    Object.entries(groupedDicts.topicSentiment).forEach(([key, count]) => {
      const [topic, sentiment] = JSON.parse(key) as [string, string];

      data.push([{ name: topic }, { name: sentiment }, count]);
    });

    Object.entries(groupedDicts.sentimentHuman).forEach(([key, count]) => {
      const [sentiment, human] = JSON.parse(key) as [string, boolean];

      data.push([
        { name: sentiment },
        {
          name: human ? "Transferred to human" : "Handled by AI Agent",
          isBold: true,
        },
        count,
      ]);
    });

    return data;
  }, [analyticsData]);

  return sentimentAnalysisData;
};
